import React, { useEffect, useState } from 'react';
import CKEditor from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import styled from 'styled-components';
import {
  surface, canvas, textPrimary, textSecondary,
  borderDefault, borderSubtle, info, infoLight, danger,
} from '../../tokens/colors';
import { radiusMd } from '../../tokens/radii';
import { fontFamily } from '../../tokens/typography';

interface RichTextProps {
  value?: string;
  onChange?: (html: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  minHeight?: number;
  compact?: boolean;
}

const TOOLBAR_FULL = [
  'heading', '|',
  'bold', 'italic', 'underline', '|',
  'bulletedList', 'numberedList', '|',
  'link', '|',
  'undo', 'redo',
];

const TOOLBAR_COMPACT = [
  'bold', 'italic', '|',
  'link', '|',
  'undo', 'redo',
];

const Wrap = styled.div<{ $error: boolean; $disabled: boolean; $minHeight: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .ck.ck-toolbar {
    background: ${canvas};
    border: none;
    border-bottom: 0.5px solid ${borderSubtle};
    border-radius: ${radiusMd} ${radiusMd} 0 0;
    padding: 4px 6px;
  }

  .ck.ck-toolbar .ck-button {
    border-radius: 4px;
    color: ${textSecondary};
    min-width: 28px;
    min-height: 28px;
    padding: 2px 6px;
  }

  .ck.ck-toolbar .ck-button:hover:not(.ck-disabled) {
    background: ${borderSubtle};
    color: ${textPrimary};
  }

  .ck.ck-toolbar .ck-button.ck-on {
    background: ${infoLight};
    color: ${info};
  }

  .ck.ck-editor {
    border: 0.5px solid ${({ $error }) => $error ? '#F09595' : borderDefault};
    border-radius: ${radiusMd};
    overflow: hidden;
  }

  .ck.ck-editor:focus-within {
    border-color: ${({ $error }) => $error ? '#F09595' : info};
    box-shadow: 0 0 0 3px ${({ $error }) => $error ? '#FCEBEB' : infoLight};
  }

  .ck.ck-editor__main .ck-editor__editable {
    border: none;
    background: ${surface};
    font-family: ${fontFamily};
    font-size: 13px;
    color: ${textPrimary};
    min-height: ${({ $minHeight }) => $minHeight}px;
    padding: 10px 14px;
    line-height: 1.6;
    opacity: ${({ $disabled }) => $disabled ? 0.55 : 1};
    pointer-events: ${({ $disabled }) => $disabled ? 'none' : 'auto'};

    &.ck-focused {
      outline: none !important;
      box-shadow: none !important;
    }

    p { margin: 0 0 6px; }
    p:last-child { margin-bottom: 0; }
    a { color: ${info}; text-decoration: underline; }
    strong { font-weight: 600; }
  }
`;

const Label = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${textSecondary};
`;

const ErrorMsg = styled.span`
  font-size: 11px;
  color: ${danger};
`;

// ─────────────────────────────────────────────────────────────────────────────
//  Componente
//
//  Segue o mesmo padrão do RichText original:
//  - useState para currentValue (não useRef)
//  - data={currentValue} inicializa o CKEditor com o conteúdo correto
//  - onChange atualiza o estado E notifica o pai via callback
//  - useEffect sincroniza quando value muda externamente (pre-fill via API)
// ─────────────────────────────────────────────────────────────────────────────

export const RichText: React.FC<RichTextProps> = ({
  value = '',
  onChange,
  label,
  placeholder,
  error,
  disabled = false,
  minHeight = 120,
  compact = false,
}) => {
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => {
    if (value !== currentValue) {
      setCurrentValue(value);
    }
  }, [value]);

  const handleChange = (_event: any, editor: any) => {
    const data = editor.getData();
    setCurrentValue(data);
    onChange?.(data);
  };

  return (
    <Wrap $error={!!error} $disabled={disabled} $minHeight={minHeight}>
      {label && <Label>{label}</Label>}
      <CKEditor
        editor={ClassicEditor}
        data={currentValue}
        config={{
          toolbar: compact ? TOOLBAR_COMPACT : TOOLBAR_FULL,
          placeholder: placeholder || '',
        }}
        onInit={() => {}}
        onChange={handleChange}
        disabled={disabled}
      />
      {error && <ErrorMsg>{error}</ErrorMsg>}
    </Wrap>
  );
};

export default RichText;
